import { useEffect, useState, useCallback } from "react";
import { useDeviceListLive } from "@/hooks/useDeviceLive";
import { ScanLine, ArrowRight, Trash2, Cpu } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import QrScanDialog from "@/components/devices/QrScanDialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";

// Same tolerant parsing as the mobile scanner: a JSON payload with a
// `serial` field, or a bare serial string if the code isn't JSON.
function parseQrData(raw) {
  const trimmed = (raw || "").trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed.serial === "string" && parsed.serial.trim()) return parsed.serial.trim();
    return null;
  } catch {
    return trimmed;
  }
}

export default function RegisterDevice() {
  const [devices, setDevices] = useState([]);
  const [serial, setSerial] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => api.get("/devices").then((r) => setDevices(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useDeviceListLive(setDevices);
  useEffect(() => { load(); }, [load]);

  const register = async (raw) => {
    const value = raw.trim().toUpperCase();
    if (!value) return;
    setBusy(true);
    try {
      const { data } = await api.post("/devices/register", { serial: value });
      toast.success("Device registered", { description: `${data.serial} can now be connected to from the mobile app.` });
      setSerial("");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const handleScanned = (raw) => {
    const parsed = parseQrData(raw);
    setScanOpen(false);
    if (!parsed) { toast.error("That QR code isn't a valid KyraLink meter code."); return; }
    register(parsed);
  };

  const submit = (e) => { e.preventDefault(); register(serial); };

  const remove = async () => {
    try {
      await api.delete(`/devices/${removing.id}`);
      toast.success("Device removed", { description: `${removing.serial} was unregistered.` });
      setRemoving(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="h-full overflow-y-auto kl-scroll-light bg-white text-kl-black" data-testid="register-device-page">
      <div className="max-w-1xl mx-auto p-8">
        <p className="kl-label">Provisioning</p>
        <h1 className="font-display font-light text-3xl mt-1">Register device</h1>
        <p className="text-sm text-kl-mutedLight mt-2 leading-relaxed max-w-2xl">
          Register a meter's serial number before it can be paired in the field — enter it manually or scan the QR code
          printed on the unit. This step only records the serial as known to KyraLink; client, address and site are
          assigned later, during setup from the mobile app. Only a registered serial can be connected to.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 mt-8 items-start">
          <div className="kl-panel flex flex-col" data-testid="register-device-list">
            <div className="px-5 h-14 border-b border-kl-lineLight flex items-center justify-between shrink-0">
              <h2 className="font-display text-lg">Registered devices</h2>
              <span className="font-mono text-xs text-kl-mutedLight">{devices.length}</span>
            </div>
            {devices.length === 0 ? (
              <p className="p-6 text-sm text-kl-mutedLight" data-testid="register-list-empty">No devices registered yet.</p>
            ) : (
              <ul className="divide-y divide-kl-lineLight max-h-[520px] overflow-y-auto kl-scroll-light">
                {devices.map((d) => (
                  <li key={d.id} className="kl-row flex items-center gap-4 px-5 h-16" data-testid={`register-row-${d.serial}`}>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${d.status === "active" ? "bg-kl-active" : "bg-kl-inactive"}`} title={d.status} />
                    <Cpu size={16} className="text-kl-mutedLight shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm truncate">{d.serial}</div>
                      <div className="text-xs text-kl-mutedLight truncate">
                        {d.client_name && d.client_name !== d.serial ? d.client_name : "No site assigned"} · registered {fmt.date(d.installed_at)}
                      </div>
                    </div>
                    <button onClick={() => setRemoving(d)} data-testid={`register-delete-${d.serial}`} className="p-2 text-kl-mutedLight hover:text-white hover:bg-kl-inactive transition-colors duration-150 shrink-0" title="Remove device">
                      <Trash2 size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <form onSubmit={submit} className="kl-panel p-6">
            <span className="kl-label">Device serial</span>
            <div className="flex gap-2 mt-1">
              <input
                data-testid="register-serial-input"
                className="kl-input-box flex-1"
                style={{ fontFamily: "JetBrains Mono" }}
                placeholder="KL-ESP32-XXXXXX"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                autoCapitalize="characters"
                autoComplete="off"
              />
              <button type="button" onClick={() => setScanOpen(true)} className="kl-btn-outline shrink-0 px-3" data-testid="register-scan-button" title="Scan QR code">
                <ScanLine size={15} />
              </button>
            </div>
            <button disabled={busy || !serial.trim()} className="kl-btn-primary w-full h-11 mt-4" data-testid="register-submit-button">
              {busy ? "Registering…" : "Register device"} <ArrowRight size={15} />
            </button>
          </form>
        </div>
      </div>

      <QrScanDialog open={scanOpen} onOpenChange={setScanOpen} onDetected={handleScanned} />
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Remove this device?" description={removing ? `${removing.serial} will be unregistered — the mobile app will no longer be able to connect to it, and any stored readings will be deleted.` : ""} confirmLabel="Remove" onConfirm={remove} />
    </div>
  );
}
